import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setOrder(res.data.order);
          setState("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (res.data.status === "expired" || attempts >= 6) {
          setState("failed");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setState("failed");
      }
    };
    poll();
  }, [sessionId, clearCart]);

  return (
    <div className="px-6 max-w-2xl mx-auto py-24 text-center min-h-[60vh] flex flex-col items-center justify-center">
      {state === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-[#B85C38] animate-spin" />
          <h1 className="font-serif text-3xl text-[#2D372B] mt-6">Confirming your order…</h1>
          <p className="text-[#7C897B] mt-2">One moment while we check your payment.</p>
        </>
      )}
      {state === "paid" && (
        <div data-testid="order-confirmed">
          <CheckCircle2 className="w-16 h-16 text-[#4A5D4E] mx-auto" />
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2D372B] mt-6 tracking-tight">Thank you!</h1>
          <p className="text-[#4A5D4E] mt-4 text-lg">
            Your order {order?.order_number && <span className="font-medium">{order.order_number}</span>} is confirmed.
          </p>
          {order && (
            <p className="text-[#7C897B] mt-1">Total paid: {formatPrice(order.total)}</p>
          )}
          <p className="text-[#7C897B] mt-4 max-w-md mx-auto">
            We'll hand pack your goods and send a note to {order?.customer?.email}. Thanks for supporting our little orchard.
          </p>
          <Link to="/shop" className="inline-block mt-8 bg-[#B85C38] hover:bg-[#9A4A2B] text-white uppercase tracking-widest text-sm px-8 py-4 transition-colors rounded-sm" data-testid="continue-shopping">
            Continue Shopping
          </Link>
        </div>
      )}
      {state === "failed" && (
        <div data-testid="order-failed">
          <XCircle className="w-16 h-16 text-[#B85C38] mx-auto" />
          <h1 className="font-serif text-4xl text-[#2D372B] mt-6">Payment not completed</h1>
          <p className="text-[#7C897B] mt-3">We couldn't confirm your payment. If you were charged, please contact us.</p>
          <Link to="/shop" className="inline-block mt-8 border border-[#B85C38] text-[#B85C38] hover:bg-[#B85C38] hover:text-white uppercase tracking-widest text-sm px-8 py-3.5 transition-colors rounded-sm">
            Back to Shop
          </Link>
        </div>
      )}
    </div>
  );
}
